import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Modal, message as messageApi } from 'antd';
import { useTranslation } from 'react-i18next';
import { showError } from 'utils/exceptionUtils';
import Loading from '../../../components/Loading';
import DspFinanceInfoForm, { DspFinanceInfoFormValues } from '../../../components/dsp/DspFinanceInfoForm';
import { useMyDspFinanceInfo } from '../../../graphql/queries/dspFinanceInfo';
import { useSaveMyDspFinanceInfo } from '../../../graphql/mutations/dspFinanceInfo';
import { DspFinanceInfoType } from '../../../graphql/types/DspFinanceInfoType';

/**
 * DSP Owner 端财务信息自助维护页。
 *
 * 账期天数与退出延迟天数属 SwiftX 侧商务条款，由总部维护，本页不展示也不提交。
 */
const DeliveryDspFinanceInfoPage: React.FC = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm<DspFinanceInfoFormValues>();
    const [financeInfo, setFinanceInfo] = useState<DspFinanceInfoType | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // 校验到确认框关闭期间置为 true，防止快速双击弹出多个确认框
    const confirmingRef = useRef(false);

    const { data, loading, error, refetch } = useMyDspFinanceInfo();
    const [saveMyDspFinanceInfo] = useSaveMyDspFinanceInfo();

    useEffect(() => {
        if (data?.dspFinanceInfo) {
            setFinanceInfo(data.dspFinanceInfo);
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            showError(error);
        }
    }, [error]);

    const locked = financeInfo?.completed === true;

    const submit = async (values: DspFinanceInfoFormValues) => {
        setSubmitting(true);
        try {
            const result = await saveMyDspFinanceInfo({
                variables: {
                    input: {
                        vendorCountry: values.vendorCountry,
                        federalTaxClassification: values.federalTaxClassification,
                        taxIdType: values.taxIdType,
                        taxIdNumber: values.taxIdNumber,
                        bankName: values.bankName,
                        accountType: values.accountType,
                        accountNo: values.accountNo,
                        routingNo: values.routingNo,
                        vendorAddress: values.vendorAddress
                    }
                }
            });
            if (result.data?.saveMyDspFinanceInfo) {
                setFinanceInfo(result.data.saveMyDspFinanceInfo);
            }
            messageApi.success(t('dspFinanceInfo.messages.saveSuccess') as string);
            refetch();
        } catch (e: any) {
            showError(e);
            // 任何失败均刷新；若后端已判定锁定，页面随之切为只读
            refetch();
        } finally {
            setSubmitting(false);
        }
    };

    const handleSave = async () => {
        if (submitting || confirmingRef.current) return;
        confirmingRef.current = true;
        let values: DspFinanceInfoFormValues;
        try {
            values = await form.validateFields();
        } catch {
            // 校验失败由表单自身提示
            confirmingRef.current = false;
            return;
        }
        // 保存后即锁定，提交前二次确认
        Modal.confirm({
            content: t('dspFinanceInfo.messages.submitConfirm'),
            onOk: async () => {
                await submit(values);
                confirmingRef.current = false;
            },
            onCancel: () => {
                confirmingRef.current = false;
            }
        });
    };

    if (loading && !financeInfo) {
        return <Loading spinning={loading} />;
    }

    return (
        <div className="bg-white rounded-lg shadow-sm p-4">
            <Card title={t('dspFinanceInfo.title')} bordered={false}>
                <div className="max-w-3xl">
                    {locked && (
                        <Alert
                            type="info"
                            showIcon
                            className="mb-4"
                            message={t('dspFinanceInfo.messages.lockedHint')}
                        />
                    )}
                    <DspFinanceInfoForm form={form} financeInfo={financeInfo} disabled={locked} />
                    {!locked && (
                        <div className="flex justify-start mt-4">
                            <Button type="primary" onClick={handleSave} loading={submitting}>
                                {t('components.button.save')}
                            </Button>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default DeliveryDspFinanceInfoPage;
